import { Person } from '@mui/icons-material';
import { Box } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { useLoggedInEmployeeId } from '~/components/Auth';
import { SolaForceDataGrid } from '~/components/SolaForceDataGrid';
import {
  AnalyticsReportDefaultProps,
  AnalyticsReportEmployeeScope,
  AnalyticsReportRoleScope,
  AnalyticsReportScope,
  AnalyticsReportTypes,
  useBirthdaysReportData,
} from '~/data/reports';
import {
  ReportColumnsVisibility,
  ReportDataExport,
  ReportDatasetLayout,
  ReportDatasetRowCount,
  ReportDatasetSelector,
  ReportDateRangeSelector,
  ReportDateRangeSelectorMode,
  ReportViewLayout,
  useReportDataset,
} from '~/scenes/PortalModules/Analytics/common';

import { NextDaysFilterDropdown } from './NextDaysFilterDropdown';
import { useBirthdaysReport } from './useBirthdaysReport';

export const BirthdaysReport = () => {
  const { t } = useTranslation();

  const apiRef = useGridApiRef();

  const loggedInEmployeeId = useLoggedInEmployeeId();

  const defaultReportSettings: AnalyticsReportDefaultProps<object> = {
    reportType: {
      type: AnalyticsReportTypes.Team,
      role: AnalyticsReportRoleScope.Manager,
      employeeId: loggedInEmployeeId,
      scope: AnalyticsReportScope.Direct,
    },
    params: {
      startDate: moment().startOf('year').toDate(),
      endDate: moment().toDate(),
      empScope: AnalyticsReportEmployeeScope.AllEmployees,
    },
  };

  const reportRequestProps = useReportDataset({
    selection: defaultReportSettings,
    config: {
      options: {
        types: [
          AnalyticsReportTypes.Team,
          AnalyticsReportTypes.HR,
          AnalyticsReportTypes.Unit,
        ],
        roles: [
          AnalyticsReportRoleScope.Manager,
          AnalyticsReportRoleScope.AltManager,
        ],
      },
    },
  });

  const { reportSelection } = reportRequestProps;

  const { data, isLoading, failedToLoad } =
    useBirthdaysReportData(reportSelection);

  const { table, nextDays, onNextDaysFilterChange } = useBirthdaysReport({
    data,
  });

  return (
    <ReportViewLayout
      heading={t('laBirthdays')}
      slots={{
        toolbar: {
          main: (
            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <ReportDatasetLayout>
                <ReportDatasetSelector
                  {...reportRequestProps}
                  isLoading={isLoading}
                  failedToLoad={failedToLoad}
                  renderCurrentParams={({ params }) => {
                    return (
                      <>{`${moment(params?.startDate).format('L')} - ${moment(
                        params?.endDate,
                      ).format('L')}`}</>
                    );
                  }}
                  renderParamsFields={({ params, onChange }) => {
                    return (
                      <ReportDateRangeSelector
                        startDate={params.startDate}
                        endDate={params.endDate}
                        onRangeChange={(startDate, endDate) =>
                          onChange({
                            ...params,
                            startDate: startDate ?? params.startDate,
                            endDate: endDate ?? params.endDate,
                          })
                        }
                        mode={ReportDateRangeSelectorMode.DateRange}
                      />
                    );
                  }}
                />
                <ReportDatasetRowCount
                  count={data.length}
                  description={t('laTotalRecords')}
                  icon={<Person />}
                />
              </ReportDatasetLayout>
              <NextDaysFilterDropdown
                value={nextDays}
                onChange={onNextDaysFilterChange}
              />
            </Box>
          ),
          actions: (
            <>
              <ReportColumnsVisibility
                apiRef={apiRef}
                columnGroupingModel={table.columnGroupingModel}
                isLoading={isLoading}
              />
              <ReportDataExport
                title={t('laBirthdays')}
                startDate={reportSelection.params?.startDate}
                endDate={reportSelection.params?.endDate}
                apiRef={apiRef}
                isLoading={isLoading}
              />
            </>
          ),
        },
      }}
    >
      <SolaForceDataGrid
        apiRef={apiRef}
        columns={table.columnsConfigs}
        rows={table.rows}
        columnGroupingModel={table.columnGroupingModel}
        experimentalFeatures={{ columnGrouping: true }}
        loading={isLoading}
        density={'compact'}
        getRowId={(row) => row.employeeId}
        pinnedColumns={{
          left: ['employeeId', 'firstName', 'lastName'],
        }}
      />
    </ReportViewLayout>
  );
};
